/**
 * Tower Defense — shared wave algebra.
 *
 * Imported by both:
 *   • services/game-server/src/rooms/TowerDefenseArena.ts (authoritative)
 *   • apps/web/src/pages/TowerDefenseGame.tsx (local sim, lockstep)
 *
 * Determinism is the contract: given the same (waveNumber, seed) every
 * client + server MUST produce the identical QueuedSpawn list. The server
 * picks the seed once per room and pins both clients to it. Anti-cheat then
 * boils down to checking that reported HP deltas / wave-clear timestamps
 * stay within the bounds that THIS wave can produce.
 */

export type TdEnemyType = "grunt" | "runner" | "tank" | "boss";

export interface TdEnemyBase {
  hp: number;
  /** Cells/second along the path. */
  speed: number;
  reward: number;
  size: number;
  /** Core HP lost when this enemy leaks (reaches the end of the path). */
  damage: number;
  /** Display tint (client-only — server ignores). */
  color: string;
  name: string;
}

export const TD_ENEMY_BASE: Record<TdEnemyType, TdEnemyBase> = {
  grunt: { hp: 36, speed: 1.7, reward: 5, size: 11, damage: 1, color: "#C9C9CC", name: "Grunt" },
  runner: { hp: 22, speed: 3.1, reward: 6, size: 9, damage: 1, color: "#FFB380", name: "Runner" },
  tank: { hp: 150, speed: 1.0, reward: 14, size: 14, damage: 3, color: "#5C5D62", name: "Tank" },
  boss: { hp: 850, speed: 0.78, reward: 80, size: 18, damage: 10, color: "#F2581E", name: "Boss" },
};

// ─────────────────────────────────────────────────────────────────────────
// Board geometry + path. Shared so server and client agree on what's a
// valid tower cell, where enemies travel, and how to project distance
// along the path to a screen position.
// ─────────────────────────────────────────────────────────────────────────

export const TD_BOARD = {
  cols: 18,
  rows: 10,
  cell: 56,
  width: 18 * 56,
  height: 10 * 56,
} as const;

export const TD_WAYPOINTS: ReadonlyArray<readonly [number, number]> = [
  [-1, 2],
  [4, 2],
  [4, 6],
  [9, 6],
  [9, 1],
  [13, 1],
  [13, 7],
  [3, 7],
  [3, 9],
  [16, 9],
  [16, 4],
  [TD_BOARD.cols, 4],
];

export const TD_PATH_CELLS: ReadonlySet<string> = (() => {
  const set = new Set<string>();
  for (let i = 0; i < TD_WAYPOINTS.length - 1; i++) {
    const [x1, y1] = TD_WAYPOINTS[i];
    const [x2, y2] = TD_WAYPOINTS[i + 1];
    if (x1 === x2) {
      const lo = Math.min(y1, y2);
      const hi = Math.max(y1, y2);
      for (let y = lo; y <= hi; y++) set.add(`${x1},${y}`);
    } else {
      const lo = Math.min(x1, x2);
      const hi = Math.max(x1, x2);
      for (let x = lo; x <= hi; x++) set.add(`${x},${y1}`);
    }
  }
  return set;
})();

export interface TdPathSegment {
  ax: number;
  ay: number;
  bx: number;
  by: number;
  /** Length of this segment in pixels. */
  len: number;
  /** Cumulative distance at the START of this segment. */
  start: number;
}

export const TD_SEGMENTS: { segs: TdPathSegment[]; total: number } = (() => {
  const segs: TdPathSegment[] = [];
  let total = 0;
  for (let i = 0; i < TD_WAYPOINTS.length - 1; i++) {
    const a = TD_WAYPOINTS[i];
    const b = TD_WAYPOINTS[i + 1];
    const ax = (a[0] + 0.5) * TD_BOARD.cell;
    const ay = (a[1] + 0.5) * TD_BOARD.cell;
    const bx = (b[0] + 0.5) * TD_BOARD.cell;
    const by = (b[1] + 0.5) * TD_BOARD.cell;
    const len = Math.hypot(bx - ax, by - ay);
    segs.push({ ax, ay, bx, by, len, start: total });
    total += len;
  }
  return { segs, total };
})();

/** Project a path distance (px from segment[0].ax,ay) into board coords. */
export function tdPosAtDistance(d: number): { x: number; y: number } {
  const { segs, total } = TD_SEGMENTS;
  if (d <= 0) return { x: segs[0].ax, y: segs[0].ay };
  if (d >= total) {
    const last = segs[segs.length - 1];
    return { x: last.bx, y: last.by };
  }
  for (const s of segs) {
    if (d >= s.start && d <= s.start + s.len) {
      const t = (d - s.start) / s.len;
      return { x: s.ax + (s.bx - s.ax) * t, y: s.ay + (s.by - s.ay) * t };
    }
  }
  return { x: 0, y: 0 };
}

// ─────────────────────────────────────────────────────────────────────────
// Tower types. Authoritative on the server (validates `place_tower` calls
// against this table), used as display + cost source on the client.
// ─────────────────────────────────────────────────────────────────────────

export type TdTowerType = "sentry" | "rifle" | "pulse" | "frost";

export interface TdTowerStats {
  id: TdTowerType;
  name: string;
  cost: number;
  /** Range in cells (multiply by TD_BOARD.cell for pixels). */
  range: number;
  /** Shots per second. */
  fireRate: number;
  damage: number;
  /** Projectile travel speed in cells/sec. */
  projectileSpeed: number;
  /** Splash radius in cells. 0 = single target. */
  splash: number;
  /** Slow factor [0..1) applied on hit. */
  slow: number;
  /** Seconds the slow lingers. */
  slowDuration: number;
  desc: string;
  accent: string;
  upgrade: {
    cost: number;
    damage: number;
    range?: number;
    fireRate?: number;
    splash?: number;
    slow?: number;
    slowDuration?: number;
  };
}

export const TD_TOWER_TYPES: Record<TdTowerType, TdTowerStats> = {
  sentry: {
    id: "sentry",
    name: "Sentry",
    cost: 50,
    range: 2.4,
    fireRate: 1.6,
    damage: 10,
    projectileSpeed: 14,
    splash: 0,
    slow: 0,
    slowDuration: 0,
    desc: "Single target",
    accent: "#F2581E",
    upgrade: { cost: 80, damage: 22, range: 2.8, fireRate: 2.0 },
  },
  rifle: {
    id: "rifle",
    name: "Rifle",
    cost: 120,
    range: 4.6,
    fireRate: 0.65,
    damage: 50,
    projectileSpeed: 28,
    splash: 0,
    slow: 0,
    slowDuration: 0,
    desc: "Long range",
    accent: "#F2581E",
    upgrade: { cost: 180, damage: 110, range: 5.4, fireRate: 0.85 },
  },
  pulse: {
    id: "pulse",
    name: "Pulse",
    cost: 90,
    range: 1.9,
    fireRate: 3.0,
    damage: 6,
    projectileSpeed: 18,
    splash: 0.9,
    slow: 0,
    slowDuration: 0,
    desc: "Splash",
    accent: "#F2581E",
    upgrade: { cost: 130, damage: 12, splash: 1.1, fireRate: 3.6 },
  },
  frost: {
    id: "frost",
    name: "Frost",
    cost: 80,
    range: 2.1,
    fireRate: 1.0,
    damage: 2,
    projectileSpeed: 16,
    splash: 0,
    slow: 0.5,
    slowDuration: 1.8,
    desc: "Slows enemies",
    accent: "#9FE7FF",
    upgrade: { cost: 110, damage: 5, slow: 0.65, slowDuration: 2.4 },
  },
};

export const TD_TOWER_ORDER: TdTowerType[] = ["sentry", "rifle", "pulse", "frost"];

/** Sell value (fraction of total invested). */
export const TD_SELL_RATIO = 0.7;

export interface TdQueuedSpawn {
  type: TdEnemyType;
  /** Seconds from wave start when this enemy should appear. */
  atTime: number;
  /** HP after wave scaling. */
  hp: number;
  /** USDT reward after wave scaling. */
  reward: number;
}

/**
 * Match-wide tuning that both ends rely on.
 */
export const TD_CONSTANTS = {
  initialMoney: 200,
  initialHp: 20,
  /** Server tick rate for the room state (lobby + ready + waves). Game
   *  sim itself runs at the client's rAF; the server doesn't simulate. */
  serverTickHz: 5,
  /** Pre-wave-1 ready window before forced-start countdown kicks in. */
  readyTimeoutSec: 60,
  /** Forced-start countdown once readyTimeoutSec elapses with only one
   *  player ready. */
  forceStartSec: 10,
  /** Inactivity grace before treating disconnects as a forfeit. */
  disconnectGraceSec: 30,
} as const;

/** Wave-bonus payout for clearing wave N (in USDT in-game currency). */
export function tdWaveBonus(n: number): number {
  return 22 + n * 4;
}

/** Wave from which the build phase is cut to a short, fixed window. */
export const TD_FAST_PREP_WAVE = 10;

/** Seconds the build phase lasts before wave N auto-fires. */
export function tdPrepDurationFor(nextWave: number): number {
  // From wave 10 on, halve the build phase down to a flat 10s so the
  // mid/late game keeps the pressure on instead of idling at ~30s.
  if (nextWave >= TD_FAST_PREP_WAVE) return 10;
  return Math.min(30, 15 + (nextWave - 1) * 1.5);
}

/**
 * Wave at which "sudden death" begins. Up to here the curve is the classic
 * linear ramp; from this wave on, difficulty doubles and keeps compounding
 * so the late game stops feeling comfortable.
 */
export const TD_SUDDEN_DEATH_WAVE = 30;

/** Waves between each successive doubling once sudden death is active. */
export const TD_SUDDEN_DEATH_DOUBLE_EVERY = 5;

/**
 * Difficulty multiplier applied to enemy HP. 1x before the threshold, then
 * a sudden 2x the instant sudden death starts and another doubling every
 * TD_SUDDEN_DEATH_DOUBLE_EVERY waves after (x2 @30, x4 @35, x8 @40 …) — a
 * runaway ramp that turns the late game into a race against the core.
 */
export function tdSuddenDeathMultiplier(n: number): number {
  if (n < TD_SUDDEN_DEATH_WAVE) return 1;
  const wavesIn = n - TD_SUDDEN_DEATH_WAVE;
  return 2 * Math.pow(2, wavesIn / TD_SUDDEN_DEATH_DOUBLE_EVERY);
}

/** Wave from which each wave gets harder: more monsters and more HP. */
export const TD_HARDER_WAVE = 10;
/** Extra monster-count multiplier applied from TD_HARDER_WAVE on (+10%). */
export const TD_HARDER_COUNT_MUL = 1.1;
/** Extra HP multiplier applied from TD_HARDER_WAVE on (+15%). */
export const TD_HARDER_HP_MUL = 1.15;
/** Special wave that spawns a double-boss gauntlet. */
export const TD_DOUBLE_BOSS_WAVE = 20;

/**
 * Linear-congruential PRNG seeded by (waveNumber, roomSeed). Identical to
 * the legacy `buildWave(n, seed)` in TowerDefenseGame.tsx (kept here as
 * the single source of truth).
 */
export function buildTdWave(n: number, seed: number): TdQueuedSpawn[] {
  const out: TdQueuedSpawn[] = [];
  let t = 0;
  // From wave 10 on, send 10% more monsters per wave.
  let base = 8 + Math.floor(n * 1.9);
  if (n >= TD_HARDER_WAVE) base = Math.round(base * TD_HARDER_COUNT_MUL);
  let r = (n * 9301 + seed * 49297 + 233280) % 233280;
  const next = (): number => {
    r = (r * 9301 + 49297) % 233280;
    return r / 233280;
  };
  for (let i = 0; i < base; i++) {
    let type: TdEnemyType = "grunt";
    const v = next();
    if (n >= 3 && v < 0.24) type = "tank";
    else if (n >= 2 && v < 0.5) type = "runner";
    out.push({ type, atTime: t, hp: 0, reward: 0 });
    t += Math.max(0.22, 0.5 - n * 0.018);
  }
  if (n > 0 && n % 4 === 0) {
    // Wave 20 is a special double-boss gauntlet.
    const bossCount = n === TD_DOUBLE_BOSS_WAVE ? 2 : 1;
    for (let i = 0; i < bossCount; i++) {
      out.push({ type: "boss", atTime: t + 1.2 + i * 1.2, hp: 0, reward: 0 });
    }
  }
  if (n >= 6 && n % 6 === 0) {
    for (let i = 0; i < 4; i++) {
      out.push({
        type: "runner",
        atTime: t + 2.4 + i * 0.18,
        hp: 0,
        reward: 0,
      });
    }
  }
  // From wave 10 on, enemies carry an extra +15% HP on top of the curve.
  let hpMul = (1 + (n - 1) * 0.3) * tdSuddenDeathMultiplier(n);
  if (n >= TD_HARDER_WAVE) hpMul *= TD_HARDER_HP_MUL;
  return out.map((e) => ({
    ...e,
    hp: Math.round(TD_ENEMY_BASE[e.type].hp * hpMul),
    reward: Math.round(TD_ENEMY_BASE[e.type].reward * (1 + (n - 1) * 0.08)),
  }));
}

/**
 * Upper bound on damage a player can take in wave N, used for anti-cheat
 * sanity checks on hp_update messages. If a client reports an HP drop
 * larger than this, the server can ignore the message (or count it as
 * a violation). Computed by summing each enemy's leak damage if zero
 * towers were built.
 */
export function tdMaxLeakDamageForWave(n: number, seed: number): number {
  return buildTdWave(n, seed).reduce(
    (sum, e) => sum + TD_ENEMY_BASE[e.type].damage,
    0,
  );
}

/** Total spawn count of a wave — useful for progress UI sync. */
export function tdWaveTotalCount(n: number, seed: number): number {
  return buildTdWave(n, seed).length;
}
